// Shared helpers for the Hikvision setup wizard's control steps.

// Physical inputs are wired to the NVR itself, not to a camera channel - unlike optical/
// thermal events, they're not scoped to specific channels at all.
const NO_CHANNEL_GROUPS = new Set(["inputs"]);
const THERMAL_GROUP = "thermal_perimeter";

// How the pin arms/disarms when zones aren't used - "rules" picks Hikvision events (rules),
// "oneKey" (one key disarm) controls the Hikvision's alarm outputs instead.
export const ARM_METHODS = { RULES: "rules", ONE_KEY: "oneKey" };

// Fallback for older backends that don't send `label`/`group` yet.
const EVENT_NAME_MAP = {
    "IO": "Alarm Input (Physical)",
    "linedetection": "Line Crossing",
    "fielddetection": "Intrusion Detection",
    "VMD": "Motion Detection",
    "behaviorRule": "Thermal Perimeter Rules",
    "regionentrance": "Region Entrance",
    "regionexiting": "Region Exiting",
};

// Hidden for now - fire/temperature events aren't exposed in the picker yet,
// though arm/disarm still works if an older pin already has them configured.
const HIDDEN_EVENT_TYPES = new Set(["thermometry", "tmpa", "temperature", "temperatureintervalmeasurement"]);

// Normalises hikvisionLogin's `data` array into the events (rules) offered by the wizard.
export const normalizeHikvisionEvents = (events = []) => events
    .filter(event => !HIDDEN_EVENT_TYPES.has(event.eventType))
    .map(event => ({
        ...event,
        displayName: event.label ?? EVENT_NAME_MAP[event.eventType] ?? event.eventType,
        group: event.group ?? "other",
        groupLabel: event.groupLabel ?? "Other events",
    }));

// Event groups covered by the selected rules - e.g. "optical", "thermal_perimeter", "inputs".
export const getSelectedGroups = (selectedEvents) => [...new Set(selectedEvents.map(event => event.group))];

export const requiresChannels = (groups) => groups.some(group => !NO_CHANNEL_GROUPS.has(group));

// The "Alarm Input" rule needs the Hikvision's alarm inputs (login response ioInputs) picked.
export const includesAlarmInput = (selectedEvents) => selectedEvents.some(event => event.eventType === "IO");

// Thermal channels are only offered for the thermal group, regular channels for everything else.
export const getChannelChoices = (groups, channels, thermalChannels) => {
    const thermalIds = new Set(thermalChannels.map(channel => channel.id));
    const includesThermal = groups.includes(THERMAL_GROUP);
    const includesNonThermal = groups.some(group => group !== THERMAL_GROUP && !NO_CHANNEL_GROUPS.has(group));
    return channels.filter(channel => thermalIds.has(channel.id) ? includesThermal : includesNonThermal);
};

// Builds the addHikvision fields - extraField2 stores whichever applies, so eventsToHandle
// carries a relay control object instead of events for one key disarm / gates.
// TODO: confirm inputIds with the backend
export const buildHikvisionControl = ({ usesOneKeyDisarm, selectedEvents, selectedChannels, selectedOutputIds, selectedInputIds }) => ({
    eventsToHandle: usesOneKeyDisarm
        ? [{ controlLevel: "alarm_output", outputIds: selectedOutputIds }]
        : selectedEvents,
    channelIds: (usesOneKeyDisarm || !requiresChannels(getSelectedGroups(selectedEvents))) ? null : selectedChannels,
    inputIds: (usesOneKeyDisarm || !includesAlarmInput(selectedEvents)) ? null : selectedInputIds,
});

export const toggleIn = (list, value) => list.includes(value) ? list.filter(x => x !== value) : [...list, value];
